import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@nosible/shared/components/ui/alert-dialog";
import { CreateApiKeyForm } from "./CreateApiKeyForm";
import { trpc } from "@/admin/lib/trpc";
import { Key, Plus, Trash2, Clock } from "lucide-react";

interface SubApiKeysProps {
  subscriptionId: string;
}

export function SubApiKeys({ subscriptionId }: SubApiKeysProps) {
  const {
    data: apiKeys,
    isLoading: apiKeysLoading,
    refetch: refetchApiKeys,
  } = trpc.subscription.apiKey.listApiKeys.useQuery(
    { subscriptionId },
    { enabled: !!subscriptionId }
  );

  const deactivateApiKeyMutation =
    trpc.subscription.apiKey.deactivateApiKey.useMutation({
      onSuccess: () => {
        refetchApiKeys();
      },
    });

  const handleDeactivateKey = (keyId: string) => {
    deactivateApiKeyMutation.mutate({ keyId, subscriptionId });
  };

  const renderDeactivateButton = (apiKey: any) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={deactivateApiKeyMutation.isPending}
          title="Deactivate key"
          className="text-red-400 hover:text-red-300"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate API Key</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to deactivate "{apiKey.name}"? Any applications
            using this key will stop working.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handleDeactivateKey(apiKey.id)}
            className="bg-red-600 hover:bg-red-700"
          >
            Deactivate
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <Card>
      <CardHeader>
        <div className="admin-card-heading">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Keys
            </CardTitle>
            <CardDescription>
              Manage API keys for accessing the Nosible API
            </CardDescription>
          </div>
          <CreateApiKeyForm
            subscriptionId={subscriptionId}
            onSuccess={() => {
              refetchApiKeys();
            }}
          />
        </div>
      </CardHeader>
      <CardContent>
        {apiKeysLoading ? (
          <div className="text-chromium-mist">Loading API keys...</div>
        ) : apiKeys?.length === 0 ? (
          <div className="text-center py-8 text-chromium-mist">
            <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <>
            <div className="admin-api-key-table">
              <Table className="admin-wide-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys?.map((apiKey: any) => (
                <TableRow key={apiKey.id}>
                  <TableCell className="font-medium text-silica">
                    {apiKey.name}
                    {apiKey.description && (
                      <p className="text-xs text-chromium-mist truncate max-w-[200px]">
                        {apiKey.description}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {apiKey.lastUsed
                        ? new Date(apiKey.lastUsed).toLocaleString()
                        : "Never"}
                    </div>
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {apiKey.createdAt
                      ? new Date(apiKey.createdAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {renderDeactivateButton(apiKey)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
              </Table>
            </div>
            <div className="admin-api-key-list">
              {apiKeys?.map((apiKey: any) => (
                <article className="admin-api-key-list__item" key={apiKey.id}>
                  <div>
                    <strong>{apiKey.name}</strong>
                    {apiKey.description && <p>{apiKey.description}</p>}
                  </div>
                  <dl>
                    <div>
                      <dt>Last used</dt>
                      <dd>
                        <Clock className="h-3 w-3" />
                        {apiKey.lastUsed
                          ? new Date(apiKey.lastUsed).toLocaleString()
                          : "Never"}
                      </dd>
                    </div>
                    <div>
                      <dt>Created</dt>
                      <dd>
                        {apiKey.createdAt
                          ? new Date(apiKey.createdAt).toLocaleDateString()
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                  {renderDeactivateButton(apiKey)}
                </article>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
